import { useEffect, useState, type FormEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { nettoyerNumeroWhatsapp } from "@/utils/whatsapp";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_TYPE_PIECE_OPTIONS,
  type AgenceLocataire,
  type AgenceLocataireInput,
  type AgenceTypePiece,
} from "@/pages/agence/data/types";

interface LocataireFormProps {
  open: boolean;
  onClose: () => void;
  initial?: AgenceLocataire;
}

interface FormState {
  prenom: string;
  nom: string;
  telephone: string;
  whatsapp: string;
  email: string;
  adresse: string;
  profession: string;
  typePiece: AgenceTypePiece | "";
  numeroPiece: string;
  contactUrgence: string;
  notes: string;
}

const EMPTY_STATE: FormState = {
  prenom: "",
  nom: "",
  telephone: "",
  whatsapp: "",
  email: "",
  adresse: "",
  profession: "",
  typePiece: "",
  numeroPiece: "",
  contactUrgence: "",
  notes: "",
};

export default function LocataireForm({ open, onClose, initial }: LocataireFormProps) {
  const { addLocataire, updateLocataire } = useAgenceData();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        prenom: initial.prenom,
        nom: initial.nom,
        telephone: initial.telephone,
        whatsapp: initial.whatsapp,
        email: initial.email,
        adresse: initial.adresse,
        profession: initial.profession,
        typePiece: initial.typePiece,
        numeroPiece: initial.numeroPiece,
        contactUrgence: initial.contactUrgence,
        notes: initial.notes,
      });
    } else {
      setForm(EMPTY_STATE);
    }
    setErrors({});
  }, [open, initial]);

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const resetAndClose = () => {
    setForm(EMPTY_STATE);
    setErrors({});
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!form.prenom.trim()) nextErrors.prenom = "Le prénom est obligatoire.";
    if (!form.nom.trim()) nextErrors.nom = "Le nom est obligatoire.";
    if (!form.telephone.trim()) nextErrors.telephone = "Le téléphone est obligatoire.";
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = "Indiquez une adresse email valide.";
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: AgenceLocataireInput = {
      prenom: form.prenom.trim(),
      nom: form.nom.trim(),
      telephone: form.telephone.trim(),
      whatsapp: nettoyerNumeroWhatsapp(form.whatsapp),
      email: form.email.trim(),
      adresse: form.adresse.trim(),
      profession: form.profession.trim(),
      typePiece: form.typePiece,
      numeroPiece: form.numeroPiece.trim(),
      contactUrgence: form.contactUrgence.trim(),
      notes: form.notes.trim(),
    };

    if (initial) {
      updateLocataire(initial.id, input);
    } else {
      addLocataire(input);
    }
    resetAndClose();
  };

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      size="lg"
      title={initial ? "Modifier le locataire" : "Ajouter un locataire"}
      description="Renseignez la fiche du locataire. Le prénom, le nom et le téléphone sont obligatoires."
      footer={
        <>
          <button
            type="button"
            onClick={resetAndClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="agence-locataire-form"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-check-line text-base" aria-hidden="true" />
            Enregistrer le locataire
          </button>
        </>
      }
    >
      <form id="agence-locataire-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field label="Prénom" htmlFor="agence-loc-prenom" required error={errors.prenom}>
            <Input
              id="agence-loc-prenom"
              value={form.prenom}
              onChange={(event) => update("prenom", event.target.value)}
              placeholder="Ex. Awa"
              maxLength={60}
              invalid={Boolean(errors.prenom)}
            />
          </Field>

          <Field label="Nom" htmlFor="agence-loc-nom" required error={errors.nom}>
            <Input
              id="agence-loc-nom"
              value={form.nom}
              onChange={(event) => update("nom", event.target.value)}
              placeholder="Ex. Traoré"
              maxLength={60}
              invalid={Boolean(errors.nom)}
            />
          </Field>

          <Field label="Téléphone" htmlFor="agence-loc-telephone" required error={errors.telephone}>
            <Input
              id="agence-loc-telephone"
              type="tel"
              value={form.telephone}
              onChange={(event) => update("telephone", event.target.value)}
              placeholder="Ex. +226 70 00 00 00"
              maxLength={40}
              invalid={Boolean(errors.telephone)}
            />
          </Field>

          <Field
            label="Téléphone WhatsApp"
            htmlFor="agence-loc-whatsapp"
            hint="Facultatif. Format international, ex. +226 70 00 00 00."
          >
            <Input
              id="agence-loc-whatsapp"
              type="tel"
              value={form.whatsapp}
              onChange={(event) => update("whatsapp", event.target.value)}
              placeholder="+226 XX XX XX XX"
              maxLength={40}
            />
          </Field>

          <Field label="Email" htmlFor="agence-loc-email" error={errors.email}>
            <Input
              id="agence-loc-email"
              type="email"
              value={form.email}
              onChange={(event) => update("email", event.target.value)}
              placeholder="Ex. awa@exemple.com"
              maxLength={120}
              invalid={Boolean(errors.email)}
            />
          </Field>

          <Field label="Profession" htmlFor="agence-loc-profession">
            <Input
              id="agence-loc-profession"
              value={form.profession}
              onChange={(event) => update("profession", event.target.value)}
              placeholder="Ex. Commerçante"
              maxLength={80}
            />
          </Field>

          <Field label="Contact d'urgence" htmlFor="agence-loc-urgence" hint="Nom et numéro d'une personne à joindre.">
            <Input
              id="agence-loc-urgence"
              value={form.contactUrgence}
              onChange={(event) => update("contactUrgence", event.target.value)}
              placeholder="Ex. Moussa Traoré — +226 76 00 00 00"
              maxLength={120}
            />
          </Field>

          <Field label="Adresse" htmlFor="agence-loc-adresse" className="md:col-span-2">
            <Input
              id="agence-loc-adresse"
              value={form.adresse}
              onChange={(event) => update("adresse", event.target.value)}
              placeholder="Adresse actuelle du locataire"
              maxLength={160}
            />
          </Field>

          <Field label="Type de pièce d'identité" htmlFor="agence-loc-type-piece">
            <Select
              id="agence-loc-type-piece"
              value={form.typePiece}
              onChange={(event) => update("typePiece", event.target.value)}
              options={AGENCE_TYPE_PIECE_OPTIONS}
              placeholder="Sélectionnez un type"
            />
          </Field>

          <Field label="Numéro de pièce" htmlFor="agence-loc-numero-piece">
            <Input
              id="agence-loc-numero-piece"
              value={form.numeroPiece}
              onChange={(event) => update("numeroPiece", event.target.value)}
              placeholder="Numéro figurant sur la pièce"
              maxLength={60}
            />
          </Field>

          <Field label="Notes" htmlFor="agence-loc-notes" className="md:col-span-2">
            <Textarea
              id="agence-loc-notes"
              value={form.notes}
              onChange={(event) => update("notes", event.target.value)}
              placeholder="Remarques, garanties, historique de location..."
              className="min-h-[96px]"
              maxLength={600}
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}